import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  CircularProgress,
  Box,
} from '@mui/material';
import { ImageField, useNotify } from 'react-admin';
import { postJson } from '../../utils/api';

type RunaGiftCardsDialogProps = {
  open: boolean;
  onClose: () => void;
  refresh: Function;
};

type RunaGiftCardsResponse = {
  giftCards: {
    code: string;
    name: string;
    gift_card: {
      assets: {
        card_image_url: string;
      };
    };
  }[];

  pagination: {
    cursors: {
      after: string | null;
    };
    page: {
      limit: number;
    };
  };
};

export const RunaGiftCardsDialog: React.FC<RunaGiftCardsDialogProps> = ({
  open,
  onClose,
  refresh,
}) => {
  const [giftCards, setGiftCards] = useState<
    RunaGiftCardsResponse['giftCards']
  >([]);
  const [addedGiftCards, setAddedGiftCards] = useState<string[]>([]);
  const [selectedGiftCards, setSelectedGiftCards] = useState<string[]>([]);
  const [after, setAfter] = useState<string | null>(null);
  const [prevAfter, setPrevAfter] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [addingToBits, setAddingToBits] = useState<boolean>(false);
  const notify = useNotify();

  const fetchGiftCards = async (cursor?: string) => {
    setLoading(true);
    setPrevAfter(after);

    try {
      const response = await postJson('/admin/gift-card/runa', {
        after: cursor,
      });

      const { data } = await response.json();
      const { giftCards: newGiftCards, pagination } =
        data as RunaGiftCardsResponse;
      setGiftCards((prevGiftCards) => [...prevGiftCards, ...newGiftCards]);
      setAfter(pagination.cursors.after);
    } catch (error) {
      console.error('Failed to fetch gift cards:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && (prevAfter !== after || after === null)) {
      fetchGiftCards();
    }
  }, [open]);

  const handleNextPage = () => {
    if (after) {
      fetchGiftCards(after);
    }
  };

  const handleClose = () => {
    setGiftCards([]);
    setAfter(null);
    setSelectedGiftCards([]);
    onClose();
  };

  const toggleSelectGiftCard = (code: string) => {
    if (!addedGiftCards.includes(code)) {
      setSelectedGiftCards([code]);
    }
  };

  const handleAddToBits = async () => {
    if (selectedGiftCards.length === 0) {
      return;
    }

    setAddingToBits(true);
    try {
      await postJson('/admin/gift-card/runa/add', {
        giftCards: selectedGiftCards,
      });

      setAddedGiftCards((prevAdded) => [...prevAdded, ...selectedGiftCards]);
      setSelectedGiftCards([]);
      notify('Gift card was added to the Bits hidden list', {
        type: 'success',
      });
    } catch (error) {
      notify('Failed to add gift card to Bits', { type: 'error' });
      console.error(error);
    } finally {
      setAddingToBits(false);
      refresh();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>Gift Cards</DialogTitle>
      <DialogContent sx={{ overflowX: 'hidden' }}>
        <Grid container spacing={2} sx={{ overflow: 'hidden' }}>
          {giftCards.map((giftCard) => {
            const isSelected = selectedGiftCards.includes(giftCard.code);
            const isAdded = addedGiftCards.includes(giftCard.code);

            return (
              <Grid item xs={12} sm={12} md={6} lg={4} key={giftCard.code}>
                <div
                  onClick={() => toggleSelectGiftCard(giftCard.code)}
                  style={{
                    border: '2px solid',
                    borderColor: isAdded
                      ? 'grey'
                      : isSelected
                        ? 'blue'
                        : '#ddd',
                    borderRadius: '8px',
                    padding: '10px',
                    textAlign: 'center',
                    cursor: isAdded ? 'not-allowed' : 'pointer',
                    position: 'relative',
                    transition: '0.3s',
                    backgroundColor: isSelected
                      ? 'rgba(0, 123, 255, 0.1)'
                      : isAdded
                        ? 'rgba(128, 128, 128, 0.3)'
                        : '',
                  }}
                >
                  <ImageField
                    source="gift_card.assets.card_image_url"
                    record={giftCard}
                    label="Gift Card"
                    sx={{ maxWidth: '100%' }}
                  />
                  <p>{giftCard.name}</p>

                  {isSelected && !isAdded && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 123, 255, 0.1)',
                        borderRadius: '8px',
                        pointerEvents: 'none',
                      }}
                    />
                  )}
                </div>
              </Grid>
            );
          })}
        </Grid>

        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              zIndex: 1000,
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        <Button
          onClick={handleAddToBits}
          disabled={selectedGiftCards.length === 0 || addingToBits}
          color="primary"
          variant="contained"
        >
          {addingToBits ? 'Adding...' : 'Add to Bits'}
        </Button>
        <Button
          onClick={handleNextPage}
          disabled={!after || loading || addingToBits}
          color="primary"
          variant="contained"
        >
          Load More
        </Button>
      </DialogActions>
    </Dialog>
  );
};
